import { useMemo } from 'react';
import Big from 'big.js';
import {
  useAccountsRests,
  useAccountsStore,
  type MarginAccount,
  type RegularAccount,
} from '@/entities/account';
import { useAssetsIcons } from '@/shared/api/queries/useAssetsIconsDictionary';
import { DEFAULT_BASE_ASSET, NUMERIC_CONFIG } from '@/shared/config';
import {
  applyAmountPrecision,
  applyPricePrecision,
  formatReceivedAmount,
  formatReceivedPrice,
} from '@/shared/lib';
import {
  TRADING_ACCOUNT_TYPES,
  TRADING_ASSETS_FILTER_ALL,
  type TradingAccountType,
  type TradingAssetAccountEntry,
  type TradingAssetRow,
  type TradingAssetsFilter,
} from './types';
import { defaultRowsComparator } from './sorting';

type AssetMeta = {
  code: string;
  name: string;
  precision?: number;
  group?: TradingAssetRow['group'];
};

type RawEntry = {
  asset: AssetMeta;
  entry: Omit<TradingAssetAccountEntry, 'amountText' | 'baseAmountText'>;
};

const collectSpotEntries = (accounts: Array<RegularAccount>): Array<RawEntry> =>
  accounts.flatMap(account =>
    account.assets.map(asset => ({
      asset,
      entry: {
        accountType: 'spot' as const,
        accNum: account.accNum,
        tradingAccKey: account.tradingAccKey,
        amount: asset.available + asset.reserved,
        baseAmount: (asset.baseAvailable ?? 0) + (asset.baseReserved ?? 0),
      },
    }))
  );

const calcFreeMargin = (
  equity: number | undefined,
  margin: number | undefined
): number | undefined =>
  equity == null || margin == null
    ? undefined
    : new Big(equity).minus(margin).toNumber();

const collectMarginEntries = (
  accountType: TradingAccountType,
  accounts: Array<MarginAccount>
): Array<RawEntry> =>
  accounts.flatMap(account => {
    const asset = account.assets[0];

    if (!asset) {
      return [];
    }

    return [
      {
        asset,
        entry: {
          accountType,
          accNum: account.accNum,
          accName: account.accName,
          tradingAccKey: account.tradingAccKey,
          amount: asset.equity ?? 0,
          baseAmount: asset.baseEquity ?? 0,
          equity: asset.equity,
          margin: asset.margin,
          freeMargin: calcFreeMargin(asset.equity, asset.margin),
          marginLevel: asset.marginLevel,
        },
      },
    ];
  });

const useTradingAssetRows = (filter: TradingAssetsFilter) => {
  const { spot, leverage, invest, earn } = useAccountsStore();
  const { isLoading: isAccountsRestsLoading } = useAccountsRests();
  const { assetsIconsData } = useAssetsIcons();

  const typeSource = { spot, leverage, invest, earn };

  const baseSource = spot ?? leverage ?? invest ?? earn;
  const baseCode = baseSource?.baseCode ?? DEFAULT_BASE_ASSET;
  const basePrecission =
    baseSource?.basePrecission ?? NUMERIC_CONFIG.precision.default;

  const openTypes = TRADING_ACCOUNT_TYPES.filter(
    type => (typeSource[type]?.accounts.length ?? 0) > 0
  );

  const rows: Array<TradingAssetRow> = useMemo(() => {
    const entries: Array<RawEntry> = [
      ...collectSpotEntries(spot?.accounts ?? []),
      ...collectMarginEntries('leverage', leverage?.accounts ?? []),
      ...collectMarginEntries('invest', invest?.accounts ?? []),
      ...collectMarginEntries('earn', earn?.accounts ?? []),
    ];

    const filtered =
      filter === TRADING_ASSETS_FILTER_ALL
        ? entries
        : entries.filter(({ entry }) => entry.accountType === filter);

    const byCode = new Map<
      string,
      { asset: AssetMeta; entries: Array<RawEntry['entry']> }
    >();

    filtered.forEach(({ asset, entry }) => {
      const record = byCode.get(asset.code);

      if (record) {
        record.entries.push(entry);
      } else {
        byCode.set(asset.code, { asset, entries: [entry] });
      }
    });

    const assetRows = Array.from(byCode.values()).map(
      ({ asset, entries: assetEntries }) => {
        const precision = asset.precision ? asset.precision : basePrecission;

        const sortedEntries = [...assetEntries].sort(
          (a, b) =>
            TRADING_ACCOUNT_TYPES.indexOf(a.accountType) -
            TRADING_ACCOUNT_TYPES.indexOf(b.accountType)
        );

        const types = TRADING_ACCOUNT_TYPES.filter(type =>
          sortedEntries.some(entry => entry.accountType === type)
        );

        const amount = applyAmountPrecision(
          sortedEntries.reduce((sum, entry) => sum + entry.amount, 0),
          precision,
          'credit'
        );
        const baseAmount = applyPricePrecision(
          sortedEntries.reduce((sum, entry) => sum + entry.baseAmount, 0),
          basePrecission
        );

        return {
          code: asset.code,
          name: asset.name,
          icon: assetsIconsData?.value?.[asset.code],
          group: asset.group,
          amount,
          amountText: formatReceivedAmount(amount),
          baseAmount,
          baseAmountText: formatReceivedPrice(baseAmount),
          types,
          accounts: sortedEntries.map(entry => {
            const entryAmount = applyAmountPrecision(
              entry.amount,
              precision,
              'credit'
            );
            const entryBaseAmount = applyPricePrecision(
              entry.baseAmount,
              basePrecission
            );

            return {
              ...entry,
              amount: entryAmount,
              amountText: formatReceivedAmount(entryAmount),
              baseAmount: entryBaseAmount,
              baseAmountText: formatReceivedPrice(entryBaseAmount),
            };
          }),
          isExpandable: !(types.length === 1 && types[0] === 'spot'),
        };
      }
    );

    return assetRows.sort(defaultRowsComparator);
  }, [spot, leverage, invest, earn, filter, basePrecission, assetsIconsData]);

  return {
    rows,
    openTypes,
    hasTradingAccounts: openTypes.length > 0,
    baseCode,
    basePrecission,
    isLoading: isAccountsRestsLoading || spot === null,
  };
};

export { useTradingAssetRows };
